import { JSX, useMemo, useState } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  Row,
  useReactTable,
} from "react-table-8.10.7";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { TableUI } from "components/design/TableUI";
import Slider from "components/DesignSystem/Slider/Slider";
import {
  RecognitionWaterfallKind,
  RecognitionWaterfallParty,
  RecognitionWaterfallPeriod,
} from "store/apis/recognitionWaterfall";
import { formatDate } from "utils/formatDate";
import { EMPTY_CELL, LINE_ITEM_FALLBACK_LABEL, LABELS } from "./constants";
import { ItemDetails, SelectedItem } from "./ItemDetails";
import {
  COLUMN_KIND,
  getColumnAmount,
  getColumnLabel,
  WaterfallColumn,
} from "./waterfallRows";

export type SelectedCell = {
  cellId: string;
  party: RecognitionWaterfallParty;
  column: WaterfallColumn;
};

type PartyCellDetailsProps = {
  kind: RecognitionWaterfallKind;
  periods: RecognitionWaterfallPeriod[];
  selected: SelectedCell;
  onClose: () => void;
};

type ItemRow = {
  itemId: string;
  documentId: string;
  documentNumber: string;
  documentDate: string;
  description: string;
  billingPeriod: string | null;
  amount: number;
};

const getBillingPeriod = (start: string | null, end: string | null) =>
  start && end ? `${formatDate(start)} - ${formatDate(end)}` : null;

// Every line item of the party with an amount in the clicked column
const toItemRows = (
  party: RecognitionWaterfallParty,
  column: WaterfallColumn
): ItemRow[] =>
  party.documents.flatMap((document) =>
    document.items
      .map((item) => ({
        itemId: item.item_id,
        documentId: document.document_id,
        documentNumber: document.document_number || EMPTY_CELL,
        documentDate: document.document_date,
        description: item.description || LINE_ITEM_FALLBACK_LABEL,
        billingPeriod: getBillingPeriod(
          item.billing_period_start_date,
          item.billing_period_end_date
        ),
        amount: getColumnAmount(item, column),
      }))
      .filter((itemRow) => itemRow.amount !== 0)
  );

const columnHelper = createColumnHelper<ItemRow>();

const useItemColumns = (documentHeader: string, amountHeader: string) =>
  useMemo(
    () => [
      columnHelper.accessor("description", {
        header: "Name",
        size: 40,
        cell: ({ row }) => (
          <div className="t-flex t-flex-col t-gap-0.5">
            <span className="t-text-body-sm">{row.original.description}</span>
            <span className="t-text-body-sm t-text-text-30">
              {row.original.documentNumber}
            </span>
          </div>
        ),
      }),
      columnHelper.accessor("documentDate", {
        header: documentHeader,
        size: 30,
        cell: ({ row }) => (
          <div className="t-flex t-flex-col t-gap-0.5">
            <span className="t-text-body-sm">
              {formatDate(row.original.documentDate)}
            </span>
            {row.original.billingPeriod && (
              <span className="t-text-body-sm t-text-text-30">
                {row.original.billingPeriod}
              </span>
            )}
          </div>
        ),
      }),
      columnHelper.accessor("amount", {
        header: () => <div className="t-text-right">{amountHeader}</div>,
        size: 30,
        cell: ({ getValue }) => (
          <div className="t-text-right">
            <AmountSuperScript amount={getValue()} />
          </div>
        ),
      }),
    ],
    [documentHeader, amountHeader]
  );

export const PartyCellDetails = ({
  kind,
  periods,
  selected,
  onClose,
}: PartyCellDetailsProps): JSX.Element => {
  const labels = LABELS[kind];
  const [selectedItem, setSelectedItem] = useState<SelectedItem | null>(null);
  const columnLabel = getColumnLabel(selected.column, periods);
  const isDeferred = selected.column.kind === COLUMN_KIND.DEFERRED;
  const header = `${labels.recognized} from ${selected.party.party_name} ${
    isDeferred ? "deferred" : "recognised"
  } ${columnLabel}`;
  const amountHeader = isDeferred ? "Deferred amount" : "Recognised amount";
  const columns = useItemColumns(labels.documentDate, amountHeader);

  const data = useMemo(
    () => toItemRows(selected.party, selected.column),
    [selected]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose();
  };
  const handleOpenItem = ({ original }: Row<ItemRow>) =>
    setSelectedItem({
      itemId: original.itemId,
      documentId: original.documentId,
      documentNumber: original.documentNumber,
      description: original.description,
    });
  const handleItemOpenChange = (open: boolean) => {
    if (!open) setSelectedItem(null);
  };

  return (
    <Slider.Root open onOpenChange={handleOpenChange} modal={false}>
      <Slider.Content useCustomOverlay>
        <Slider.Header>
          <Slider.Title>{header}</Slider.Title>
          <Slider.Close />
        </Slider.Header>
        <Slider.Body>
          <TableUI sticky={false} table={table} onRowClick={handleOpenItem} />
          <div className="t-flex t-justify-between t-px-3 t-py-2 t-text-body-sm t-font-semibold">
            <span>Total</span>
            <AmountSuperScript
              amount={getColumnAmount(selected.party, selected.column)}
            />
          </div>
        </Slider.Body>
        {/* The item slider sits inside this one, as in the Revenue Waterfall, so its breadcrumb returns here */}
        <Slider.Root
          open={Boolean(selectedItem)}
          onOpenChange={handleItemOpenChange}
        >
          <Slider.Content>
            {selectedItem && (
              <ItemDetails
                kind={kind}
                partyName={selected.party.party_name}
                header={header}
                item={selectedItem}
              />
            )}
          </Slider.Content>
        </Slider.Root>
      </Slider.Content>
    </Slider.Root>
  );
};
